import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CheckCircle2,
  XCircle,
  Edit,
  Save,
  Undo,
  Trash2,
  Loader2,
  FileText,
  AlertCircle,
  Link2,
} from 'lucide-react';
import {
  validationService,
  entityTypeService,
  type ValidationDetail,
  type EntityTypeSummary,
  type ExtractedEntity,
} from '@/services/kbService';
import EmbeddingsTab from './EmbeddingsTab';
import KnowledgeGraphTab from './KnowledgeGraphTab';

import { logger } from '@/utils/logger';
import { BRAND_NAME } from '@/utils/branding';
interface DocumentDetailModalProps {
  documentId: string | null;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onReject?: () => void;
}

interface EntityCorrection {
  entity_id: string;
  original_type: string;
  corrected_type: string;
  original_value?: string;
  corrected_value?: string;
}

const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({
  documentId,
  visible,
  onClose,
  onSuccess,
  onReject,
}) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ValidationDetail | null>(null);
  const [activeTab, setActiveTab] = useState('metadata');
  const [isEditing, setIsEditing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');

  // Form fields
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [author, setAuthor] = useState('');
  const [tags, setTags] = useState('');

  // Custom metadata JSON editing
  const [customMetadata, setCustomMetadata] = useState<string>('{}');
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // Entity editing state
  const [entityEditMode, setEntityEditMode] = useState(false);
  const [entityCorrections, setEntityCorrections] = useState<EntityCorrection[]>([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(new Set());
  const [savingEntities, setSavingEntities] = useState(false);
  const [deletingEntities, setDeletingEntities] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [singleDeleteId, setSingleDeleteId] = useState<string | null>(null);

  // Entity types from backend
  const [entityTypes, setEntityTypes] = useState<EntityTypeSummary[]>([]);
  const [typesLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      if (!documentId) return;

      setLoading(true);
      try {
        const [detailData, typesData] = await Promise.all([
          validationService.getValidationDetail(documentId),
          entityTypeService.getEntityTypeSummary(true),
        ]);

        if (!isCancelled) {
          setDetail(detailData);
          setEntityTypes(typesData);
          // Pre-fill form
          setTitle(detailData.extracted_metadata?.title || '');
          setDocumentType(detailData.document_type || detailData.extracted_metadata?.document_type || '');
          setAuthor(detailData.extracted_metadata?.author || BRAND_NAME);
          setTags(detailData.suggested_tags?.join(', ') || '');
          // Pre-fill custom metadata JSON
          setCustomMetadata(JSON.stringify(detailData.extracted_metadata || {}, null, 2));
          setMetadataError(null);
        }
      } catch (error: any) {
        if (!isCancelled) {
          logger.error('Failed to load validation detail:', error);
          if (error.response?.status === 401 || error.response?.status === 403) {
            toast.error('Session expired. Please log in again.');
          } else {
            // Show the actual error message if available
            const errorMessage = error.message || error.response?.data?.message || 'Failed to load document details';
            toast.error(errorMessage);
          }
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    if (visible && documentId) {
      loadData();
    } else {
      resetState();
    }

    return () => {
      isCancelled = true;
    };
  }, [visible, documentId]);

  const resetState = () => {
    setDetail(null);
    setActiveTab('metadata');
    setIsEditing(false);
    setApproveNotes('');
    setRejectReason('');
    setRejectNotes('');
    setEntityEditMode(false);
    setEntityCorrections([]);
    setSelectedEntityIds(new Set());
    setTitle('');
    setDocumentType('');
    setAuthor('');
    setTags('');
    setCustomMetadata('{}');
    setMetadataError(null);
    setDeletingEntities(false);
  };

  const reloadDetail = async () => {
    if (!documentId) return;

    try {
      const data = await validationService.getValidationDetail(documentId);
      setDetail(data);
      setTitle(data.extracted_metadata?.title || '');
      setDocumentType(data.document_type || data.extracted_metadata?.document_type || '');
      setAuthor(data.extracted_metadata?.author || BRAND_NAME);
      setTags(data.suggested_tags?.join(', ') || '');
      setCustomMetadata(JSON.stringify(data.extracted_metadata || {}, null, 2));
      setMetadataError(null);
    } catch (error: any) {
      logger.error('Failed to reload detail:', error);
    }
  };

  // Handle entity type change
  const handleEntityTypeChange = (entityId: string, originalType: string, newType: string) => {
    setEntityCorrections(prev => {
      const existing = prev.find(c => c.entity_id === entityId);
      if (newType === originalType) {
        // Remove correction if type is reverted to original
        return prev.filter(c => c.entity_id !== entityId);
      }
      if (existing) {
        return prev.map(c => c.entity_id === entityId
          ? { ...c, corrected_type: newType }
          : c
        );
      }
      return [...prev, { entity_id: entityId, original_type: originalType, corrected_type: newType }];
    });
  };

  // Handle entity value change
  const handleEntityValueChange = (entityId: string, originalValue: string, newValue: string) => {
    const trimmedValue = newValue.trim();
    if (!trimmedValue) return;

    setEntityCorrections(prev => {
      const existing = prev.find(c => c.entity_id === entityId);
      if (trimmedValue === originalValue) {
        if (existing && existing.corrected_type !== existing.original_type) {
          return prev.map(c => c.entity_id === entityId
            ? { ...c, original_value: undefined, corrected_value: undefined }
            : c
          );
        }
        return prev.filter(c => c.entity_id !== entityId);
      }
      if (existing) {
        return prev.map(c => c.entity_id === entityId
          ? { ...c, original_value: originalValue, corrected_value: trimmedValue }
          : c
        );
      }
      return [...prev, {
        entity_id: entityId,
        original_type: '',
        corrected_type: '',
        original_value: originalValue,
        corrected_value: trimmedValue
      }];
    });
  };

  // Get the current value for an entity (corrected if exists, otherwise original)
  const getCurrentEntityValue = (entity: ExtractedEntity): string => {
    const correction = entityCorrections.find(c => c.entity_id === entity.entity_id);
    return (correction?.corrected_value) || entity.entity_value;
  };

  // Get the current type for an entity (corrected if exists, otherwise original)
  const getCurrentEntityType = (entity: ExtractedEntity): string => {
    const correction = entityCorrections.find(c => c.entity_id === entity.entity_id);
    return correction?.corrected_type || entity.entity_type;
  };

  // Save entity corrections
  const handleSaveEntityCorrections = async () => {
    if (!documentId || entityCorrections.length === 0) return;

    setSavingEntities(true);
    try {
      const corrections = entityCorrections
        .filter(c => c.corrected_type || c.corrected_value)
        .map(c => ({
          entity_id: c.entity_id,
          corrected_type: c.corrected_type || undefined,
          corrected_value: c.corrected_value || undefined,
          action: 'modify' as const,
        }));

      await validationService.updateMetadata(documentId, {
        entity_corrections: corrections,
      });

      const typeChanges = entityCorrections.filter(c => c.corrected_type && c.corrected_type !== c.original_type).length;
      const valueChanges = entityCorrections.filter(c => c.corrected_value).length;
      const messages = [];
      if (typeChanges > 0) messages.push(`${typeChanges} type(s)`);
      if (valueChanges > 0) messages.push(`${valueChanges} value(s)`);

      toast.success(`Entity corrections saved: ${messages.join(' and ')} updated`);
      setEntityCorrections([]);
      setEntityEditMode(false);
      await reloadDetail();
    } catch (error: any) {
      logger.error('Failed to save entity corrections:', error);
      toast.error(error.response?.data?.detail || 'Failed to save entity corrections');
    } finally {
      setSavingEntities(false);
    }
  };

  // Handle single entity delete
  const handleDeleteSingleEntity = async (entityId: string) => {
    if (!documentId) return;

    setDeletingEntities(true);
    try {
      await validationService.updateMetadata(documentId, {
        entity_corrections: [{
          entity_id: entityId,
          action: 'reject',
        }],
      });

      toast.success('Entity deleted successfully');
      await reloadDetail();
      setSelectedEntityIds(prev => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });
    } catch (error: any) {
      logger.error('Failed to delete entity:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entity');
    } finally {
      setDeletingEntities(false);
      setSingleDeleteId(null);
    }
  };

  // Handle bulk entity delete
  const handleDeleteSelectedEntities = async () => {
    if (!documentId || selectedEntityIds.size === 0) return;

    setDeletingEntities(true);
    try {
      const corrections = Array.from(selectedEntityIds).map(entityId => ({
        entity_id: entityId,
        action: 'reject' as const,
      }));

      await validationService.updateMetadata(documentId, {
        entity_corrections: corrections,
      });

      toast.success(`${selectedEntityIds.size} entity(ies) deleted successfully`);
      setSelectedEntityIds(new Set());
      setDeleteConfirmOpen(false);
      await reloadDetail();
    } catch (error: any) {
      logger.error('Failed to delete entities:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entities');
    } finally {
      setDeletingEntities(false);
    }
  };

  const handleEntitySelect = (entityId: string, checked: boolean) => {
    setSelectedEntityIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(entityId);
      } else {
        next.delete(entityId);
      }
      return next;
    });
  };

  const handleSelectAllEntities = (checked: boolean) => {
    if (checked && detail) {
      setSelectedEntityIds(new Set(detail.extracted_entities.map(e => e.entity_id)));
    } else {
      setSelectedEntityIds(new Set());
    }
  };

  const handleCustomMetadataChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setCustomMetadata(value);

    try {
      JSON.parse(value);
      setMetadataError(null);
    } catch {
      setMetadataError('Invalid JSON format');
    }
  };

  const handleSaveMetadata = async () => {
    if (!documentId) return;

    // Validate custom metadata JSON
    let parsedCustomMetadata: Record<string, any> = {};
    try {
      parsedCustomMetadata = JSON.parse(customMetadata);
      setMetadataError(null);
    } catch {
      setMetadataError('Invalid JSON format. Please fix the syntax errors.');
      toast.error('Invalid JSON in Additional Metadata. Please check the format.');
      return;
    }

    setActionLoading(true);
    try {
      await validationService.updateMetadata(documentId, {
        title,
        author,
        document_type: documentType,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        custom_metadata: parsedCustomMetadata,
      });
      toast.success('Metadata updated successfully');
      setIsEditing(false);
      await reloadDetail();
    } catch (error: any) {
      logger.error('Failed to update metadata:', error);
      toast.error(error.response?.data?.detail || 'Failed to update metadata');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!documentId) return;

    setActionLoading(true);
    try {
      await validationService.approveDocument(documentId, approveNotes || undefined);
      toast.success('Document approved successfully!');
      setApproveModalVisible(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      logger.error('Failed to approve document:', error);
      toast.error('Failed to approve document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    if (!documentId) return;

    setActionLoading(true);
    try {
      await validationService.rejectDocument(documentId, rejectReason, rejectNotes || undefined);
      toast.success('Document rejected and removed from staging');
      setRejectModalVisible(false);
      if (onReject) {
        onReject();
      } else {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      logger.error('Failed to reject document:', error);
      toast.error('Failed to reject document');
    } finally {
      setActionLoading(false);
    }
  };

  const getTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      PERSON: 'bg-blue-500/15 text-blue-400',
      ORGANIZATION: 'bg-purple-500/15 text-purple-400',
      LOCATION: 'bg-emerald-500/15 text-emerald-400',
      GPE: 'bg-emerald-500/15 text-emerald-400',
      DATE: 'bg-orange-500/15 text-orange-400',
      MONEY: 'bg-amber-500/15 text-amber-400',
      EQUIPMENT: 'bg-cyan-500/15 text-cyan-400',
      CHEMICAL: 'bg-red-500/15 text-red-400',
      SERVICE: 'bg-pink-500/15 text-pink-400',
      MATERIAL: 'bg-amber-500/15 text-amber-400',
      CONTACT: 'bg-indigo-500/15 text-indigo-400',
      CUSTOMER: 'bg-lime-500/15 text-lime-400',
      PRODUCT: 'bg-rose-500/15 text-rose-400',
      SITE: 'bg-indigo-500/15 text-indigo-400',
      PROCEDURE: 'bg-muted text-muted-foreground',
      SAFETY: 'bg-red-500/15 text-red-400',
      COMPLIANCE: 'bg-orange-500/15 text-orange-400',
    };
    return colors[type.toUpperCase()] || 'bg-muted text-muted-foreground';
  };

  const getConfidenceBadgeColor = (confidence: number): string => {
    if (confidence >= 0.8) return 'bg-emerald-500/15 text-emerald-400';
    if (confidence >= 0.6) return 'bg-amber-500/15 text-amber-400';
    return 'bg-red-500/15 text-red-400';
  };

  const getConfidenceProgressColor = (confidence: number): string => {
    if (confidence >= 0.8) return 'bg-emerald-400';
    if (confidence >= 0.6) return 'bg-blue-400';
    return 'bg-red-400';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Filter out null, empty, and zero values from metadata display
  const filterMetadata = (metadata: Record<string, any>): Record<string, any> => {
    const skipWhenNullOrZero = ['page_count', 'project_id', 'sentiment'];
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(metadata)) {
      if (value === null || value === undefined || value === '') continue;
      if (skipWhenNullOrZero.includes(key) && value === 0) continue;
      result[key] = value;
    }
    return result;
  };

  const entities = detail?.extracted_entities || [];
  const relationships = detail?.suggested_relationships || [];
  const isAllSelected = entities.length > 0 && selectedEntityIds.size === entities.length;

  return (
    <>
      <Dialog open={visible} onOpenChange={() => onClose()}>
        <DialogContent
          className="dark bg-background text-foreground max-w-4xl w-[95vw] max-h-[90vh] flex flex-col overflow-hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          hideCloseButton
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Document Validation Details
              {detail && (
                <Badge
                  variant="outline"
                  className={detail.validation_status === 'pending'
                    ? 'border-orange-500/40 bg-orange-500/15 text-orange-400'
                    : 'border-blue-500/40 bg-blue-500/15 text-blue-400'
                  }
                >
                  {detail.validation_status.toUpperCase()}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Loading document details...</p>
            </div>
          ) : detail ? (
            <>
              {/* Document Info Summary */}
              <Card className="bg-muted/50 overflow-hidden">
                <CardContent className="grid grid-cols-3 gap-x-6 gap-y-2 py-4 overflow-hidden">
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="text-sm text-muted-foreground shrink-0">Filename:</span>
                    <span className="text-sm font-medium truncate">{detail.file_name?.replace(/\.[^.]+$/, '')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">File Type:</span>
                    <Badge variant="outline" className="border-border">{detail.file_type.toUpperCase()}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Size:</span>
                    <span className="text-sm">{formatFileSize(detail.file_size)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Words:</span>
                    <span className="text-sm">{detail.word_count || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Chunks:</span>
                    <span className="text-sm">{detail.chunk_count || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Entities:</span>
                    <span className="text-sm">{detail.extracted_entities.length}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Relationships:</span>
                    <span className="text-sm">{relationships.length}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Complexity:</span>
                    {detail.complexity ? (
                      <Badge variant="outline" className="border-purple-500/40 bg-purple-500/15 text-purple-400">
                        {detail.complexity}
                      </Badge>
                    ) : (
                      <span className="text-sm">N/A</span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="metadata">Metadata</TabsTrigger>
                  <TabsTrigger value="entities">
                    Entities ({entities.length})
                  </TabsTrigger>
                  <TabsTrigger value="relationships">
                    Relationships ({relationships.length})
                  </TabsTrigger>
                  <TabsTrigger value="content">Content Preview</TabsTrigger>
                </TabsList>

                {/* Metadata Tab */}
                <TabsContent value="metadata" className="space-y-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between py-3">
                      <CardTitle className="text-base">Document Metadata</CardTitle>
                      {!isEditing ? (
                        <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveMetadata} disabled={actionLoading}>
                            {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </Button>
                        </div>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          disabled={!isEditing}
                          placeholder="Document title"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Document Type</Label>
                          <Input
                            value={documentType}
                            onChange={(e) => setDocumentType(e.target.value)}
                            disabled={!isEditing}
                            placeholder="e.g., contract, report"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Owner</Label>
                          <Input
                            value={author}
                            onChange={(e) => setAuthor(e.target.value)}
                            disabled={!isEditing}
                            placeholder="Document owner"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Tags (comma-separated)</Label>
                        <Textarea
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          disabled={!isEditing}
                          placeholder="tag1, tag2, tag3"
                          rows={2}
                        />
                      </div>

                      {/* Extracted Metadata Display - Editable */}
                      {detail.extracted_metadata && Object.keys(detail.extracted_metadata).length > 0 && (
                        <div className="space-y-2">
                          <Label>Additional Extracted Metadata</Label>
                          {isEditing ? (
                            <div className="space-y-2">
                              <Textarea
                                value={customMetadata}
                                onChange={handleCustomMetadataChange}
                                rows={10}
                                className={`font-mono text-xs ${metadataError ? 'border-red-500' : ''}`}
                                placeholder='{"key": "value"}'
                              />
                              {metadataError && (
                                <p className="text-xs text-red-500">{metadataError}</p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                Edit the JSON directly. Changes will be validated and saved when you click Save.
                              </p>
                            </div>
                          ) : (
                            <pre className="bg-muted p-3 rounded-lg text-xs overflow-auto max-h-48 font-mono whitespace-pre-wrap break-all">
                              {JSON.stringify(filterMetadata(detail.extracted_metadata), null, 2)}
                            </pre>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Entities Tab */}
                <TabsContent value="entities" className="space-y-4">
                  <div className="flex items-center justify-between">
                    {!entityEditMode ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEntityEditMode(true)}
                        disabled={entities.length === 0}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Entities
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEntityEditMode(false);
                            setEntityCorrections([]);
                            setSelectedEntityIds(new Set());
                          }}
                        >
                          <Undo className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          disabled={entityCorrections.length === 0 || savingEntities}
                          onClick={handleSaveEntityCorrections}
                        >
                          {savingEntities && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          <Save className="h-4 w-4 mr-2" />
                          Save {entityCorrections.length > 0 ? `(${entityCorrections.length} changes)` : ''}
                        </Button>
                        {selectedEntityIds.size > 0 && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteConfirmOpen(true)}
                            disabled={deletingEntities}
                          >
                            {deletingEntities && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete ({selectedEntityIds.size})
                          </Button>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2">
                      {entityCorrections.length > 0 && (
                        <Badge variant="secondary" className="bg-orange-500/15 text-orange-400">
                          {entityCorrections.length} change(s)
                        </Badge>
                      )}
                      {selectedEntityIds.size > 0 && (
                        <Badge variant="secondary" className="bg-red-500/15 text-red-400">
                          {selectedEntityIds.size} selected
                        </Badge>
                      )}
                    </div>
                  </div>

                  {entities.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {entityEditMode && (
                              <TableHead className="w-12">
                                <Checkbox
                                  checked={isAllSelected}
                                  onCheckedChange={handleSelectAllEntities}
                                />
                              </TableHead>
                            )}
                            <TableHead>Type</TableHead>
                            <TableHead>Value</TableHead>
                            <TableHead>Confidence</TableHead>
                            <TableHead>Context</TableHead>
                            {entityEditMode && <TableHead className="w-16">Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entities.map((entity) => {
                            const currentType = getCurrentEntityType(entity);
                            const currentValue = getCurrentEntityValue(entity);
                            const typeChanged = currentType !== entity.entity_type;
                            const valueChanged = currentValue !== entity.entity_value;

                            return (
                              <TableRow key={entity.entity_id}>
                                {entityEditMode && (
                                  <TableCell>
                                    <Checkbox
                                      checked={selectedEntityIds.has(entity.entity_id)}
                                      onCheckedChange={(checked) =>
                                        handleEntitySelect(entity.entity_id, checked as boolean)
                                      }
                                    />
                                  </TableCell>
                                )}
                                <TableCell>
                                  {entityEditMode ? (
                                    <Select
                                      value={currentType}
                                      onValueChange={(value) => handleEntityTypeChange(entity.entity_id, entity.entity_type, value)}
                                      disabled={typesLoading}
                                    >
                                      <SelectTrigger className="w-40">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className="dark">
                                        {entityTypes.map((t) => (
                                          <SelectItem key={t.name} value={t.name}>
                                            <Badge className={getTypeColor(t.name)}>{t.label}</Badge>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <Badge className={getTypeColor(currentType)}>
                                        {currentType}
                                      </Badge>
                                      {typeChanged && (
                                        <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/30">
                                          was: {entity.entity_type}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {entityEditMode ? (
                                    <div className="space-y-1">
                                      <Input
                                        defaultValue={currentValue}
                                        onBlur={(e) => {
                                          const newValue = e.target.value.trim();
                                          if (newValue && newValue !== entity.entity_value) {
                                            handleEntityValueChange(entity.entity_id, entity.entity_value, newValue);
                                          }
                                        }}
                                        className="w-full"
                                      />
                                      {valueChanged && (
                                        <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/30">
                                          was: {entity.entity_value}
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    currentValue
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Badge className={getConfidenceBadgeColor(entity.confidence)}>
                                      {(entity.confidence * 100).toFixed(0)}%
                                    </Badge>
                                    <div className="w-16 h-1.5 rounded-full overflow-hidden bg-muted">
                                      <div
                                        className={`h-full rounded-full transition-all ${getConfidenceProgressColor(entity.confidence)}`}
                                        style={{
                                          width: `${entity.confidence * 100}%`
                                        }}
                                      />
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground max-w-48 truncate">
                                  {entity.context || 'N/A'}
                                </TableCell>
                                {entityEditMode && (
                                  <TableCell>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                      onClick={() => setSingleDeleteId(entity.entity_id)}
                                      disabled={deletingEntities}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No entities were extracted from this document.</p>
                    </div>
                  )}
                </TabsContent>

                {/* Relationships Tab */}
                <TabsContent value="relationships">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Document Relationships</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {relationships.length > 0 ? (
                        <div className="space-y-4">
                          {relationships.map((rel, index) => (
                            <Card key={index} className="bg-muted/30">
                              <CardContent className="py-4 space-y-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium">{rel.target_file_name}</span>
                                  <Badge variant="secondary" className="bg-blue-500/15 text-blue-400">
                                    {rel.relationship_type}
                                  </Badge>
                                  <Badge className={getConfidenceBadgeColor(rel.confidence)}>
                                    {(rel.confidence * 100).toFixed(0)}%
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  <strong>Reason:</strong> {rel.reason}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  <strong>Detection Method:</strong> {rel.detection_method}
                                </p>
                                {rel.metadata?.shared_entities && rel.metadata.shared_entities.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-sm text-muted-foreground mb-1">
                                      <strong>Shared Entities ({rel.metadata.shared_entity_count}):</strong>
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {rel.metadata.shared_entities.map((entity, idx) => (
                                        <Badge key={idx} variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                          {entity.name} ({entity.type})
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Link2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No related documents found.</p>
                          <p className="text-sm">Relationships are detected based on shared entities with other documents.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Content Tab */}
                <TabsContent value="content">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Content Preview (First 500 Words)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {detail.content_preview ? (
                        <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap max-h-96 overflow-auto font-mono leading-relaxed">
                          {detail.content_preview}
                        </pre>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>Content preview not available for this document.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Embeddings and Knowledge Graph tabs hidden for now */}
              </Tabs>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Unable to load document details.</p>
            </div>
          )}
          </DialogBody>

          <DialogFooter className="gap-2">
            {loading || !detail ? (
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            ) : detail.validation_status === 'pending' ? (
              <>
                <Button
                  variant="destructive"
                  onClick={() => setRejectModalVisible(true)}
                  disabled={loading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button onClick={() => setApproveModalVisible(true)} disabled={loading}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-4 w-full justify-between">
                <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/15 text-emerald-400">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Already Approved
                </Badge>
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation */}
      <AlertDialog open={approveModalVisible} onOpenChange={setApproveModalVisible}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Document</AlertDialogTitle>
            <AlertDialogDescription>
              The document will be moved to production storage and become searchable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="approveNotes">Approval Notes (optional)</Label>
            <Textarea
              id="approveNotes"
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              placeholder="Optional approval notes..."
              rows={3}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApprove} disabled={actionLoading}>
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Confirmation */}
      <AlertDialog open={rejectModalVisible} onOpenChange={setRejectModalVisible}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Document</AlertDialogTitle>
            <AlertDialogDescription>
              The document will be removed from staging and will not be stored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="rejectReason" className="text-destructive">
                Rejection Reason (Required) *
              </Label>
              <Textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection (required)"
                rows={2}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="rejectNotes">Additional Notes (Optional)</Label>
              <Textarea
                id="rejectNotes"
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="Additional notes about rejection..."
                rows={2}
                className="mt-2"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={actionLoading || !rejectReason.trim()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedEntityIds.size} Entity(ies)?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove them and their Neo4j relationships.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelectedEntities}
              disabled={deletingEntities}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingEntities && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Single Delete Confirmation */}
      <AlertDialog open={!!singleDeleteId} onOpenChange={() => setSingleDeleteId(null)}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entity?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the entity and its relationships.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => singleDeleteId && handleDeleteSingleEntity(singleDeleteId)}
              disabled={deletingEntities}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingEntities && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DocumentDetailModal;
